import { ReactElement, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { wallIsCartesian } from '@geometry'
import { hashObject } from '@scene'
import { StructuralPlanningElementFormHeader } from '@structuralPlanningComponents'
import { Button, Stack } from '@mui/material'
import CreateElementLabelField from '@editor/pages/StructuralPlanning/components/CreateElementLabelField'
import {
  applyCreateLabel,
  createLabelSchema,
} from '@editor/pages/StructuralPlanning/components/RectangularShape/components/RectangularShapeForm/components/GeometryForm/schema'
import { useIsMutating } from '@tanstack/react-query'
import { DeleteButton, HotkeyDeleteButton, HotkeySaveButton, SaveButton } from '@ui/actions'
import { Form } from '@ui/forms'
import { useElementLabel } from '@editorHooks'
import { CollapsibleGeometryBoxSection } from '@editorComponents'
import FormFields from './components/FormFields'
import WallFormScene from './components/WallFormScene'
import { wallGeometrySchema } from './schema'
import { buildFormWall, getWallFormDefaultValues, WallFormValues } from './utils'

interface Props {
  /** the saved wall, or the drawn draft (`isDraft`) */
  wall: ShapeObject
  /** saves the wall together with the connected walls moved along by "Intelligentes Verschieben" */
  handleSubmit: (wall: ShapeObject, dependentWalls: ShapeObject[]) => void
  handleDelete: () => void
  isSubmitting?: boolean
  isDeleting?: boolean
  isPlacementEditable?: boolean
  /** The wall is a drawn, not yet saved draft: shows the label field for creation. */
  isDraft?: boolean
  /** edit the wall in the scene as a 2D line (moving, extending) */
  sceneEditEnabled?: boolean
  /** if given, saves right after every move/extension in the scene (IFC importer) */
  handleSceneEditSubmit?: (wall: ShapeObject, dependentWalls: ShapeObject[]) => Promise<unknown>

  onClose?: () => void
}

/**
 * Geometry form of a wall, in 2D and 3D. The form owns the whole unsaved edit state: the typed
 * coordinates, moves and extensions in the scene (see `WallFormScene`) and the connected walls moved
 * along with it. Nothing unsaved is ever written to the model store.
 */
const WallGeometryForm = ({
  wall,
  handleSubmit,
  handleDelete,
  isSubmitting,
  isDeleting,
  isPlacementEditable = true,
  isDraft = false,
  sceneEditEnabled = false,
  handleSceneEditSubmit,
  onClose,
}: Props): ReactElement => {
  const getLabel = useElementLabel()
  const label = getLabel(wall.guid)

  const { projectId } = useParams()

  // Disable the fields while any wall geometry update for this project is running (e.g. the IFC
  // importer's save after a move in the scene), so the form cannot be edited mid-save.
  const isUpdatingWall = useIsMutating(['updateWall', projectId]) > 0

  const defaultValues = useMemo(() => getWallFormDefaultValues(wall, isDraft), [wall, isDraft])

  // a save must be a single length change or translation of the saved wall (see `wallGeometrySchema`)
  const validationContext = useMemo(
    () =>
      isDraft ? {} : { savedWall: { start: wall.shape.points[0], end: wall.shape.points[1] } },
    [isDraft, wall],
  )

  const validationSchema = useMemo(
    () => (isDraft ? wallGeometrySchema.concat(createLabelSchema) : wallGeometrySchema),
    [isDraft],
  )

  const submit = useCallback(
    (values: WallFormValues) => {
      const submittedWall = buildFormWall(wall, values.start, values.end, values.placement)

      handleSubmit(
        isDraft ? applyCreateLabel(submittedWall, values.label) : submittedWall,
        values.dependentWalls,
      )
    },
    [wall, isDraft, handleSubmit],
  )

  const submitSceneEdit = useMemo(() => {
    if (!handleSceneEditSubmit) return undefined

    return (values: WallFormValues) =>
      handleSceneEditSubmit(
        buildFormWall(wall, values.start, values.end, values.placement),
        values.dependentWalls,
      )
  }, [wall, handleSceneEditSubmit])

  const SubmitButton = isDraft || sceneEditEnabled ? HotkeySaveButton : SaveButton
  const RemoveButton = isDraft ? HotkeyDeleteButton : DeleteButton

  return (
    <Form
      // re-seed the form whenever the saved wall changes (save, split, refetch)
      key={`${wall.guid}-${hashObject(wall.shape)}-${wall.placement}`}
      onSubmit={submit}
      validationSchema={validationSchema}
      validationContext={validationContext}
      defaultValues={defaultValues}
    >
      {({ reset }) => (
        <>
          <WallFormScene
            wall={wall}
            isDraft={isDraft}
            sceneEditEnabled={sceneEditEnabled}
            onSceneEdit={submitSceneEdit}
          />
          <Stack direction="column" spacing={1}>
            {isDraft ? (
              <CreateElementLabelField />
            ) : (
              <StructuralPlanningElementFormHeader
                elementGuid={wall.guid}
                label={label}
                variant="h6"
                onClose={onClose}
              />
            )}

            <CollapsibleGeometryBoxSection label="Geometrie" defaultExpanded={true}>
              <Stack direction="column" spacing={1}>
                <FormFields
                  wall={wall}
                  isDraft={isDraft}
                  isPlacementEditable={isPlacementEditable}
                  showIntelligentWallEdit={sceneEditEnabled && wallIsCartesian(wall)}
                  disabled={isUpdatingWall}
                />

                <Stack direction="row" justifyContent="end" spacing={1}>
                  <SubmitButton
                    type="submit"
                    loading={isSubmitting}
                    fullWidth
                    disabled={isDeleting || isUpdatingWall}
                    data-cy="btn-wall-geometry-form-save"
                  >
                    Speichern
                  </SubmitButton>
                  {!isDraft && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => reset()}
                      fullWidth
                      disabled={isUpdatingWall}
                    >
                      Zurücksetzen
                    </Button>
                  )}
                  <RemoveButton
                    onClick={handleDelete}
                    size="small"
                    fullWidth
                    loading={isDeleting}
                    disabled={isSubmitting || isUpdatingWall}
                    data-cy="delete-wall-btn"
                  >
                    Löschen
                  </RemoveButton>
                </Stack>
              </Stack>
            </CollapsibleGeometryBoxSection>
          </Stack>
        </>
      )}
    </Form>
  )
}

export default WallGeometryForm
